import { Consumer } from 'src/domain/consumer';
import { Message } from 'src/domain/message';
import { OrganizationGroup } from 'src/domain/organization';

export const isTargetScopedToSingleResident = ({
    receiverId,
    sendToAll,
    groupIds,
    consumers,
    organizationGroups,
}: {
    receiverId?: string;
    sendToAll?: boolean;
    groupIds?: string[];
    consumers?: Consumer[];
    organizationGroups?: OrganizationGroup[];
}) => {
    if (receiverId) {
        return true;
    }

    if (sendToAll) {
        return false;
    }

    const targetGroupIds = groupIds?.filter(Boolean) ?? [];
    if (!targetGroupIds.length || !consumers?.length) {
        return false;
    }

    const childrenByParentId = new Map<string, string[]>();
    for (const group of organizationGroups ?? []) {
        if (!group.parentId) {
            continue;
        }

        const children = childrenByParentId.get(group.parentId) ?? [];
        children.push(group.id);
        childrenByParentId.set(group.parentId, children);
    }

    const expandedGroupIds = new Set<string>();
    const stack = [...targetGroupIds];

    while (stack.length) {
        const groupId = stack.pop();
        if (!groupId || expandedGroupIds.has(groupId)) {
            continue;
        }

        expandedGroupIds.add(groupId);
        for (const childId of childrenByParentId.get(groupId) ?? []) {
            stack.push(childId);
        }
    }

    const targetConsumerIds = new Set<string>();

    for (const consumer of consumers) {
        const isTargeted = (consumer.groupIds ?? []).some(groupId =>
            expandedGroupIds.has(groupId),
        );

        if (isTargeted) {
            targetConsumerIds.add(consumer.id);
        }

        if (targetConsumerIds.size > 1) {
            return false;
        }
    }

    return targetConsumerIds.size === 1;
};

export const isMessageScopedToSingleResident = ({
    message,
    consumers,
    organizationGroups,
}: {
    message: Message;
    consumers?: Consumer[];
    organizationGroups?: OrganizationGroup[];
}) =>
    isTargetScopedToSingleResident({
        receiverId: message.receiverId,
        sendToAll: message.target.sendToAll,
        groupIds: message.target.groupIds,
        consumers,
        organizationGroups,
    });

export const canCloseMessageCommentsForScope = ({
    message,
    consumers,
    organizationGroups,
}: {
    message: Message;
    consumers?: Consumer[];
    organizationGroups?: OrganizationGroup[];
}) =>
    Boolean(message.commentsEnabled) &&
    !isMessageScopedToSingleResident({
        message,
        consumers,
        organizationGroups,
    });
